import { LogManager } from '@src/utility/Logger';

import type { TranscriptElementWithClipSegmentAttributes } from '@src/types.ts/transcript';

const logger = LogManager.getLogger(
  'videoTemplate:SpeakerFirstAppearanceService',
);

export interface SpeakerFirstAppearance {
  speakerLabel: string;
  speakerId?: string;
  firstAppearanceTime: number;
  firstTranscriptElement: TranscriptElementWithClipSegmentAttributes;
}

/**
 * Service for detecting when speakers first appear in a video transcript.
 * Used for intelligently placing lower thirds when speakers are first introduced.
 */
export class SpeakerFirstAppearanceService {
  /**
   * Detects the first appearance of each speaker in the transcript.
   * Returns a map of speaker labels to their first appearance time.
   *
   * @param transcriptElements - Array of transcript elements with timing information
   * @param minGapSeconds - Minimum gap in seconds between speaker appearances (default: 0).
   *                        If a speaker appears within this gap after the previous speaker,
   *                        their appearance will be skipped and the next occurrence will be used.
   * @returns Array of speaker first appearances sorted by time
   *
   * @example
   * const appearances = detectFirstAppearances(transcriptElements);
   * // Returns [
   * //   { speakerLabel: 'spk_0', firstAppearanceTime: 2.5, ... },
   * //   { speakerLabel: 'spk_1', firstAppearanceTime: 15.3, ... }
   * // ]
   *
   * @example
   * // With 5 second gap requirement
   * const appearances = detectFirstAppearances(transcriptElements, 5);
   * // Skips speakers that appear within 5 seconds of the previous speaker
   */
  detectFirstAppearances(
    transcriptElements:
      | TranscriptElementWithClipSegmentAttributes[]
      | null
      | undefined,
    minGapSeconds: number = 0,
  ): SpeakerFirstAppearance[] {
    if (!Array.isArray(transcriptElements) || transcriptElements.length === 0) {
      logger.info('No transcript elements provided');
      return [];
    }

    // Map to track first appearance of each speaker
    const firstAppearanceMap = new Map<string, SpeakerFirstAppearance>();

    // Track the last accepted appearance time to enforce minimum gap
    let lastAcceptedAppearanceTime: number | null = null;

    // Iterate through transcript elements to find first speaker appearances
    for (const element of transcriptElements) {
      // Only process text elements with timing and speaker information
      // Skip removed/cut elements as they're not part of the active transcript
      if (
        element.type !== 'text' ||
        typeof element.ts !== 'number' ||
        !element.speaker_label ||
        element.state === 'removed' ||
        element.state === 'cut'
      ) {
        continue;
      }

      const speakerLabel = element.speaker_label;

      // Skip if we've already recorded this speaker's first appearance
      if (firstAppearanceMap.has(speakerLabel)) {
        continue;
      }

      // Check if this appearance is too soon after the last accepted appearance
      if (
        lastAcceptedAppearanceTime !== null &&
        element.ts - lastAcceptedAppearanceTime < minGapSeconds
      ) {
        logger.debug(`Skipping speaker appearance (too soon after previous)`, {
          speakerLabel,
          time: element.ts,
          lastAcceptedTime: lastAcceptedAppearanceTime,
          gap: element.ts - lastAcceptedAppearanceTime,
          minGap: minGapSeconds,
        });
        continue;
      }

      // Record this as the first appearance for this speaker
      firstAppearanceMap.set(speakerLabel, {
        speakerLabel,
        speakerId: element.speaker_id,
        firstAppearanceTime: element.ts,
        firstTranscriptElement: element,
      });

      // Update the last accepted appearance time
      lastAcceptedAppearanceTime = element.ts;

      logger.debug(`First appearance detected`, {
        speakerLabel,
        time: element.ts,
        text: element.value,
      });
    }

    // Convert map to array and sort by appearance time
    const appearances = Array.from(firstAppearanceMap.values()).sort(
      (a, b) => a.firstAppearanceTime - b.firstAppearanceTime,
    );

    logger.info(`Detected ${appearances.length} unique speakers`, {
      minGapSeconds,
      speakers: appearances.map(a => ({
        label: a.speakerLabel,
        time: a.firstAppearanceTime,
      })),
    });

    return appearances;
  }

  /**
   * Gets the first appearance time for a specific speaker.
   *
   * @param transcriptElements - Array of transcript elements
   * @param speakerLabel - The speaker label to search for (e.g., 'spk_0')
   * @param minGapSeconds - Minimum gap in seconds between speaker appearances (default: 0)
   * @returns The first appearance or null if speaker not found
   */
  getFirstAppearanceForSpeaker(
    transcriptElements:
      | TranscriptElementWithClipSegmentAttributes[]
      | null
      | undefined,
    speakerLabel: string,
    minGapSeconds: number = 0,
  ): SpeakerFirstAppearance | null {
    const appearances = this.detectFirstAppearances(
      transcriptElements,
      minGapSeconds,
    );
    return appearances.find(a => a.speakerLabel === speakerLabel) || null;
  }

  /**
   * Checks if a given time is within a reasonable window of a speaker's first appearance.
   * Useful for determining if a lower third should be shown at the current time.
   *
   * @param firstAppearanceTime - When the speaker first appears
   * @param currentTime - The current playback time
   * @param windowSeconds - How many seconds after first appearance to consider (default: 5)
   * @returns True if currentTime is within the window
   */
  isWithinFirstAppearanceWindow(
    firstAppearanceTime: number,
    currentTime: number,
    windowSeconds: number = 5,
  ): boolean {
    return (
      currentTime >= firstAppearanceTime &&
      currentTime <= firstAppearanceTime + windowSeconds
    );
  }
}

// Singleton instance
export const speakerFirstAppearanceService =
  new SpeakerFirstAppearanceService();
